import { Button, Callout, Card, Field, Input, PageHeader, useToast } from '@provenance/design';
import { changePasswordSchema, type ChangePasswordInput } from '@provenance/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * Change your own password.
 *
 * The consequence is unusual enough to say before they act rather than after: changing a
 * password revokes every refresh token for the account, so every other browser and device
 * is signed out. Someone doing this because they think their password is known needs that
 * to happen; someone doing it as routine housekeeping should not be surprised by it.
 *
 * The same Zod schema validates here and at the API, so the rules cannot drift apart.
 */
export function ChangePassword() {
  const toast = useToast();
  const { signOut } = useSession();
  const [done, setDone] = useState(false);

  const form = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const change = useMutation({
    mutationFn: (values: ChangePasswordInput) =>
      api.post('/api/console/auth/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      }),
    onSuccess: () => {
      setDone(true);
      form.reset();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) {
        // Attach it to the field that is wrong, rather than a banner they have to map back
        // to an input themselves.
        form.setError('currentPassword', { message: 'That is not your current password' });
        return;
      }
      toast.error(
        'Could not change your password',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      );
    },
  });

  if (done) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Password changed" />
        <Callout tone="ok" icon={<CheckCircle2 className="size-4" />} title="That is done">
          <p>
            Your password has been changed, and every other device signed in as you has been
            signed out.
          </p>
          <p className="mt-2">
            This browser is still signed in. If you changed it because someone else may know
            the old one, sign out here too.
          </p>
          <div className="mt-3">
            <Button variant="secondary" size="sm" onClick={() => void signOut()}>
              Sign out of this browser as well
            </Button>
          </div>
        </Callout>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Change password"
        lede="Choose something long rather than complicated. A short phrase you will remember beats a short string you will write down."
      />

      <Callout tone="warn" icon={<AlertTriangle className="size-4" />} title="This signs out your other devices">
        Every other browser and device signed in as you will be signed out immediately. This
        browser stays signed in.
      </Callout>

      <Card>
        <form
          className="flex max-w-md flex-col gap-3.5"
          onSubmit={form.handleSubmit((values) => change.mutate(values))}
          noValidate
        >
          <Field
            label="Your current password"
            required
            error={form.formState.errors.currentPassword?.message}
          >
            <Input type="password" autoComplete="current-password" {...form.register('currentPassword')} />
          </Field>

          <Field
            label="New password"
            required
            help="At least 12 characters."
            error={form.formState.errors.newPassword?.message}
          >
            <Input type="password" autoComplete="new-password" {...form.register('newPassword')} />
          </Field>

          <Field
            label="New password again"
            required
            error={form.formState.errors.confirmPassword?.message}
          >
            <Input type="password" autoComplete="new-password" {...form.register('confirmPassword')} />
          </Field>

          <div>
            <Button type="submit" variant="primary" loading={change.isPending}>
              Change password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
